import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from '../../../../components/Button';
import TableBox from '../../../../components/TableBox';
import Modal from '../../../../components/Modal';
import Toast from '../../../../components/Toast';
import GenerateStatementModal from '../components/GenerateStatementModal';
import Filters from './components/Filters';
import TableHeaderSection from './components/TableHeaderSection';
import Row from './components/Row';
import { Modules, RouteParamsTypes, RoleCodes, SortOrder } from '../../../../enums';
import { AuthContext } from '../../../../contexts/AuthContext';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { ManagerStatementListFilters } from '../../../../interfaces/managerStatement.interface';
import { buildHeadCells, getStartEndOfMonth } from '../../../../utils/methods';
import { VALIDATION_FIELD_MAPPING, GENERIC_LOOKUP_KEYS, HEAD_CELL_SORT_MAPPING } from '../../../../utils/appConstant';
import useSorting from '../../../../hooks/useSorting';
import usePagination from '../../../../hooks/usePagination';
import useUrlParams from '../../../../hooks/useUrlParams';
import useFetch from '../../../../hooks/useFetch';
import { usePersistentFilters } from '../../../../hooks/usePersistentFilters';
import ManagerStatementService from '../../../../services/managerStatement.service';
import UserService from '../../../../services/user.service';
import CommonService from '../../../../services/common.service';

const ManagerStatementList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loggedInUser } = useContext(AuthContext);
  const [open, setOpen] = useState(false);

  const isManagerLocked = loggedInUser?.role_code === RoleCodes.manager;

  const defaultFilters: ManagerStatementListFilters = {
    searchText: '',
    managedBy: '',
    status: '',
    ...getStartEndOfMonth(),
  };

  const { initialFilters, saveFilters, resetFilters } = usePersistentFilters<ManagerStatementListFilters>('filters_reconciliation_manager_statement', defaultFilters);

  const [filters, setFilters] = useState<ManagerStatementListFilters>(initialFilters);
  const [kpis, setKpis] = useState<{ totalAmount: string; totalAmountDue: string }>({
    totalAmount: '0.00',
    totalAmountDue: '0.00',
  });
  const [dropdownSearchData, setDropdownSearchData] = useState<any>({ managedBy: '', status: '' });
  const [counter, setCounter] = useState<number>(0);
  const [removalId, setRemovalId] = useState<number | null>(null);

  const headCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.statementNumber,
    VALIDATION_FIELD_MAPPING.month,
    VALIDATION_FIELD_MAPPING.statementDate,
    VALIDATION_FIELD_MAPPING.manager,
    VALIDATION_FIELD_MAPPING.amount,
    VALIDATION_FIELD_MAPPING.amountDue,
    VALIDATION_FIELD_MAPPING.status,
    VALIDATION_FIELD_MAPPING.actions,
  ], 'managerStatementList');

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.desc,
    defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.statementDate,
  });

  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: Number(searchParams.get('page')) || 1,
  });

  useUrlParams({
    type: Modules.reconciliation,
    uri: Modules.reconciliation + RouteParamsTypes.managerStatement,
    perPage,
    page,
    order,
    orderBy,
  });

  const { total, data: statements, loading } = useFetch({
    service: ManagerStatementService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: (HEAD_CELL_SORT_MAPPING.managerStatementList as any)[orderBy] || orderBy,
      order,
      ...filters,
    },
    dependency: [
      order, orderBy, page, perPage,
      filters.searchText, filters.managedBy, filters.status,
      filters.startDate, filters.endDate,
      counter,
    ],
    onSuccess: (response: any) => {
      setKpis({
        totalAmount: response?.total_amount ?? '0.00',
        totalAmountDue: response?.total_amount_due ?? '0.00',
      });
    },
    onError: () => {
      setKpis({ totalAmount: '0.00', totalAmountDue: '0.00' });
    },
  });

  const { data: allManagers } = useFetch({
    service: UserService,
    func: 'managerRoleLookup',
    payload: { searchText: '', is_active: '1' },
    skip: isManagerLocked,
    dependency: [isManagerLocked],
  });
  const { data: managers } = useFetch({
    service: UserService,
    func: 'managerRoleLookup',
    payload: { searchText: dropdownSearchData.managedBy, is_active: '1' },
    skip: isManagerLocked,
    dependency: [dropdownSearchData.managedBy, isManagerLocked],
  });

  const { data: allStatuses } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.managerStatementStatus, searchText: '' },
    dependency: [],
  });
  const { data: statuses } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.managerStatementStatus, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status],
  });

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleApplyFilters = useCallback((newFilters: ManagerStatementListFilters) => {
    setPage(1);
    setFilters(newFilters);
    saveFilters(newFilters);
  }, [saveFilters, setPage]);

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setFilters(defaultFilters);
    resetFilters();
  }, [resetFilters, setPage]);

  const handleGenerateClick = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleGenerated = useCallback((newStatementId: number) => {
    setOpen(false);
    navigate(`${Modules.reconciliation}${RouteParamsTypes.managerStatement}/${newStatementId}`);
  }, [navigate]);

  const handleRemoveClick = useCallback((id: number) => {
    setRemovalId(id);
  }, []);

  const handleRemoveClose = useCallback(() => {
    setRemovalId(null);
  }, []);

  const handleRemoveStatement = useCallback(async () => {
    if (!removalId) return;

    try {
      await ManagerStatementService.deleteStatement(String(removalId));
      setCounter((prev) => prev + 1);
      toast.success(<Toast message={t('reconciliation.managerStatement.list.pageTexts.table.confirmModal.deleteSuccess')} />, { icon: false });
    } catch (error: any) {
      if (error?.data?.message) {
        toast.error(<Toast message={error.data.message} />, { icon: false, toastId: 'manager-statement-delete-conflict' });
      }
    } finally {
      setRemovalId(null);
    }
  }, [removalId, t]);

  const headerSection = <TableHeaderSection total={total} kpis={kpis} />;

  const values: TableContextProvider = useMemo(() => {
    return {
      headerSection,
      row: Row,
      rowProps: { handleRemoveClick },
      data: statements || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t('reconciliation.managerStatement.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    } as TableContextProvider;
  }, [
    headerSection, statements, headCells, handleRemoveClick,
    order, orderBy, page, perPage, total, loading, t,
    setPage, setPerPage, setOrder, setOrderBy,
  ]);

  return (
    <>
      <div className="row mb-6">
        <div className="col-6">
          <h4 className="m-0 fw-bold">{t('reconciliation.managerStatement.list.pageTexts.header.title')}</h4>
          <p>{t('reconciliation.managerStatement.list.pageTexts.header.subTitle')}</p>
        </div>
        <div className="col-6 d-flex align-items-center justify-content-end">
          <Button
            className="btn btn-primary waves-effect waves-light"
            onClick={handleGenerateClick}
            text={t('reconciliation.managerStatement.list.pageTexts.header.generateButton')}
          />
        </div>
      </div>

      <Filters
        managers={managers}
        allManagers={allManagers}
        statuses={statuses}
        allStatuses={allStatuses}
        showManagerFilter={!isManagerLocked}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        initialFilters={initialFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      <TableBox tableValues={values} />

      <GenerateStatementModal
        open={open}
        onClose={handleClose}
        onGenerated={handleGenerated}
      />

      <Modal
        open={removalId !== null}
        onClose={handleRemoveClose}
        title={t('reconciliation.managerStatement.list.pageTexts.table.confirmModal.title')}
        content={t('reconciliation.managerStatement.list.pageTexts.table.confirmModal.content')}
        contentClassName="text-center"
        maxWidth="sm"
        action={
          <div className="d-flex gap-3">
            <Button
              className="btn btn-outline-secondary"
              text={t('reconciliation.managerStatement.list.pageTexts.table.confirmModal.cancelBtn')}
              onClick={handleRemoveClose}
            />
            <Button
              className="btn btn-danger"
              text={t('reconciliation.managerStatement.list.pageTexts.table.confirmModal.deleteBtn')}
              onClick={handleRemoveStatement}
            />
          </div>
        }
      />
    </>
  );
};

export default ManagerStatementList;
